/**
 * Token encryption utility using Web Crypto API
 *
 * This provides encryption for tokens stored in sessionStorage to mitigate XSS risks.
 * Note: This is defense-in-depth, not a complete solution to XSS vulnerabilities.
 * The encryption key is derived from a stable browser fingerprint.
 */

import { ErrorCategory, ErrorSeverity, createError, logError, handleRecoverableError } from '@services/errorHandling';

/**
 * Generates a stable key for the current browser session
 * Uses browser characteristics to derive a consistent key
 */
const getBrowserFingerprint = (): string => {
    const components = [
        navigator.userAgent,
        navigator.language,
        new Date().getTimezoneOffset().toString(),
        screen.colorDepth.toString(),
        screen.width.toString(),
        screen.height.toString(),
    ];
    return components.join('|');
};

/**
 * Derives a cryptographic key from the browser fingerprint
 */
const deriveKey = async (): Promise<CryptoKey> => {
    const fingerprint = getBrowserFingerprint();
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprint);

    // Hash the fingerprint to get consistent key material
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Import as a key for AES-GCM
    return crypto.subtle.importKey(
        'raw',
        hashBuffer,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
};

/**
 * Encrypts a string value
 */
export const encryptToken = async (plaintext: string): Promise<string> => {
    try {
        const key = await deriveKey();
        const encoder = new TextEncoder();
        const data = encoder.encode(plaintext);

        // Generate a random IV (Initialization Vector)
        const iv = crypto.getRandomValues(new Uint8Array(12));

        // Encrypt the data
        const encryptedBuffer = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            data
        );

        // Combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(encryptedBuffer), iv.length);

        // Convert to base64 for storage
        return btoa(String.fromCharCode(...combined));
    } catch (error) {
        const appError = createError(
            ErrorCategory.SECURITY,
            ErrorSeverity.ERROR,
            'Token encryption failed',
            error,
            { plaintextLength: plaintext.length }
        );
        logError(appError);
        throw error;  // Preserve original error and stack trace
    }
};

/**
 * Decrypts an encrypted string value
 */
export const decryptToken = async (encrypted: string): Promise<string> => {
    try {
        const key = await deriveKey();

        // Decode from base64
        const combined = Uint8Array.from(atob(encrypted), c => c.charCodeAt(0));

        // Extract IV and encrypted data
        const iv = combined.slice(0, 12);
        const data = combined.slice(12);

        // Decrypt the data
        const decryptedBuffer = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            data
        );

        // Convert back to string
        const decoder = new TextDecoder();
        return decoder.decode(decryptedBuffer);
    } catch (error) {
        const appError = createError(
            ErrorCategory.SECURITY,
            ErrorSeverity.ERROR,
            'Token decryption failed',
            error,
            { encryptedLength: encrypted.length }
        );
        logError(appError);
        throw error;  // Preserve original error and stack trace
    }
};

/**
 * Securely stores encrypted data in sessionStorage
 */
export const secureStorageSet = async (key: string, value: string): Promise<void> => {
    const encrypted = await encryptToken(value);
    sessionStorage.setItem(key, encrypted);
};

/**
 * Retrieves and decrypts data from sessionStorage
 */
export const secureStorageGet = async (key: string): Promise<string | null> => {
    const encrypted = sessionStorage.getItem(key);
    if (!encrypted) return null;

    try {
        return await decryptToken(encrypted);
    } catch (error) {
        // This is a recoverable error - corrupted data can be safely removed
        handleRecoverableError(
            error,
            ErrorCategory.STORAGE,
            `Failed to decrypt ${key}, removing corrupted data`,
            () => sessionStorage.removeItem(key),
            { key }
        );
        return null;
    }
};

/**
 * Removes data from sessionStorage
 */
export const secureStorageRemove = (key: string): void => {
    sessionStorage.removeItem(key);
};

/**
 * Clears all items from sessionStorage
 */
export const secureStorageClear = (): void => {
    sessionStorage.clear();
};
